import { lazy, Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { DashLayout } from './shell/dash-layout';
import { PlanGate } from './shell/plan-gate';
import { OwnerOnly } from './shell/owner-only';
import { DeferredPage } from './shell/deferred-page';
import { HomeScreen } from './screens/home/home-screen';
import { NotFoundScreen } from './screens/not-found/not-found-screen';
import { ListSkeleton } from './shell/skeleton';

// Route-level code splitting (perf: the entry chunk carried every screen). The landing
// screen and not-found stay in the entry bundle; every other screen is its own chunk,
// fetched on first navigation.
const BookingsScreen = lazy(() => import('./screens/bookings/bookings-screen').then((m) => ({ default: m.BookingsScreen })));
const ServicesScreen = lazy(() => import('./screens/services/services-screen').then((m) => ({ default: m.ServicesScreen })));
const BusinessScreen = lazy(() => import('./screens/business/business-screen').then((m) => ({ default: m.BusinessScreen })));
const OffersScreen = lazy(() => import('./screens/offers/offers-screen').then((m) => ({ default: m.OffersScreen })));
const InsightsScreen = lazy(() => import('./screens/insights/insights-screen').then((m) => ({ default: m.InsightsScreen })));
const InsightsLocked = lazy(() => import('./screens/insights/insights-locked').then((m) => ({ default: m.InsightsLocked })));
const TeamScreen = lazy(() => import('./screens/team/team-screen').then((m) => ({ default: m.TeamScreen })));
const PlanScreen = lazy(() => import('./screens/plan/plan-screen').then((m) => ({ default: m.PlanScreen })));
const AccountScreen = lazy(() => import('./screens/account/account-screen').then((m) => ({ default: m.AccountScreen })));
const InboxScreen = lazy(() => import('./screens/inbox/inbox-screen').then((m) => ({ default: m.InboxScreen })));
const MarketingScreen = lazy(() => import('./screens/marketing/marketing-screen').then((m) => ({ default: m.MarketingScreen })));

// A lazy screen's boundary — shaped like the page that is arriving (a blank pane
// reads as a broken navigation). Navigations run in a transition, so the current
// screen normally stays up until the next one's chunk is in.
function lazyRoute(element: ReactElement): ReactElement {
  return <Suspense fallback={<ListSkeleton />}>{element}</Suspense>;
}

// Pure wiring (CB-D2): the PRD v2.5 §7.3.3 merchant route table — 10 design pages +
// Inbox and QR studio under More (DR-37) + not-found — inside the DC70 shell.
// /insights is Insights (DC79) behind the insights entitlement (FR-B22: the locked page).
// /account is Account (DC82). Existing screens mount unchanged until redesigned:
//   /qr → Marketing (QR studio)   (/plan is My Plan, DC81; settle lives in the booking drawer)
//   /promote  → coming-later page (content deferred, DR-32)
// Folded / renamed paths redirect to their new home (§7.3.3, §14.2): Calendar, Verify,
// Check-in and Clients are folded into Bookings (DC72, DR-37) — the week view, the
// Payment check tab, the drawer's QR check-in and the search / returning-customer lookup.
// A team member's session (API-16) reaches Home, Bookings and Account only; every
// other page is wrapped in <OwnerOnly> (a staff deep link lands on Home).
export const routes: RouteObject[] = [
  {
    element: <DashLayout />,
    children: [
      { path: '/', element: <HomeScreen /> },
      { path: '/bookings', element: lazyRoute(<BookingsScreen />) },
      { path: '/services', element: <OwnerOnly>{lazyRoute(<ServicesScreen />)}</OwnerOnly> },
      { path: '/business', element: <OwnerOnly>{lazyRoute(<BusinessScreen />)}</OwnerOnly> },
      { path: '/offers', element: <OwnerOnly>{lazyRoute(<OffersScreen />)}</OwnerOnly> },
      { path: '/promote', element: <OwnerOnly><DeferredPage /></OwnerOnly> },
      {
        path: '/insights',
        element: (
          <OwnerOnly>
            <PlanGate entitlement="insights" locked={lazyRoute(<InsightsLocked />)}>
              {lazyRoute(<InsightsScreen />)}
            </PlanGate>
          </OwnerOnly>
        ),
      },
      { path: '/team', element: <OwnerOnly>{lazyRoute(<TeamScreen />)}</OwnerOnly> },
      { path: '/plan', element: <OwnerOnly>{lazyRoute(<PlanScreen />)}</OwnerOnly> },
      { path: '/account', element: lazyRoute(<AccountScreen />) },
      { path: '/inbox', element: <OwnerOnly>{lazyRoute(<InboxScreen />)}</OwnerOnly> },
      { path: '/qr', element: <OwnerOnly>{lazyRoute(<MarketingScreen />)}</OwnerOnly> },

      // Folded or renamed (each old path redirects, PRD §7.3.3).
      { path: '/calendar', element: <Navigate to="/bookings?view=calendar" replace /> },
      { path: '/verify', element: <Navigate to="/bookings?tab=payment" replace /> },
      { path: '/check-in', element: <Navigate to="/bookings" replace /> },
      { path: '/clients', element: <Navigate to="/bookings" replace /> },
      { path: '/catalog', element: <Navigate to="/services" replace /> },
      { path: '/reviews', element: <Navigate to="/services?tab=reviews" replace /> },
      { path: '/reports', element: <Navigate to="/insights" replace /> },
      { path: '/sales', element: <Navigate to="/plan?tab=billing" replace /> },
      { path: '/settings', element: <Navigate to="/business" replace /> },
      { path: '/marketing', element: <Navigate to="/qr" replace /> },
      { path: '/more', element: <Navigate to="/" replace /> },

      { path: '*', element: <NotFoundScreen /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
